import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import { CircularProgress } from "@mui/material";
import {
  ConnectedAiApp,
  NotificationType,
  OnboardingFlowType,
  PlanTypeV4,
} from "@markit/common.types";
import { hasSubscription, MARKIT_DOCS } from "@markit/common.utils";
import { addAlpha, Colors } from "../../../utils/colors";
import { GetNumericDate } from "../../../utils/GetTime";
import { getAccountState } from "../../../redux/slices/accountSlice";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import StandardBorderedContainer from "../../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import CustomTextField from "../../CustomTextField";
import RectangleButton from "../../Buttons/RectangleButton";
import LabelButton from "../../Buttons/LabelButton";
import ThreeDotsButton from "../../Buttons/ThreeDotsButton";
import { DropdownMenu, DropdownMenuItem } from "../../DropdownMenu";
import ConfirmDeleteModal from "../../Containers/ConfirmPopups/ConfirmDeleteModal";
import AlertContainer from "../../Containers/AlertContainer";
import EmptyStateButtonSubscription from "../../Buttons/EmptyState/EmptyStateButtonSubscription";
import PaymentFailedButton from "../../Subscription/PaymentFailedButton";
import { API } from "../../../API";
import { getApiErrorMessage } from "../../../utils/apiErrorUtils";
import useAiConnections from "../../../hooks/useAiConnections";
import OnboardingTourStep from "../../Onboarding/OnboardingTourStep";
import OnboardingFlowDot from "../../Onboarding/OnboardingFlowDot";
import { MCP_API_ONBOARDING_STEPS } from "../../../utils/onboardingFlowUtils";

const MCP_SERVER_URL = "https://api.markitai.com/mcp";
const MCP_DOCS_URL = MARKIT_DOCS + "/mcp-overview";

type ConnectedAppRowProps = {
  app: ConnectedAiApp;
  onDisconnectPress: (app: ConnectedAiApp) => void;
};

const ConnectedAppRow = (props: ConnectedAppRowProps) => {
  const { app, onDisconnectPress } = props;

  const dropdownItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: "Disconnect",
        icon: "ion:trash",
        key: "disconnect",
        isDestructive: true,
        onPress: () => onDisconnectPress(app),
      },
    ],
    [app, onDisconnectPress]
  );

  const subtext = useMemo(
    () =>
      [app.domain, `Connected ${GetNumericDate(app.connectedAt)}`]
        .filter(Boolean)
        .join(" · "),
    [app.connectedAt, app.domain]
  );

  return (
    <div className="AlignedRowSpaced" style={{ gap: 14, paddingInline: 14 }}>
      <div className="ColumnNormal" style={{ gap: 4, minWidth: 0 }}>
        <span className="bodyMedium">{app.name}</span>
        <span className="smallBodySubtext">{subtext}</span>
      </div>
      <div className="AlignedRow" style={{ gap: 10 }}>
        <LabelButton
          text="Active"
          color={Colors.BLACK}
          backgroundColor={addAlpha(Colors.GREEN2, 0.1)}
          borderColor={Colors.GREEN2}
          icon={<Icon icon="ion:ellipse" height={8} color={Colors.GREEN2} />}
        />
        <DropdownMenu
          dropdownItems={dropdownItems}
          trigger={["click"]}
          placement="bottomRight"
          altWidth={180}
        >
          <ThreeDotsButton onPress={() => {}} />
        </DropdownMenu>
      </div>
    </div>
  );
};

type McpManagementPanelProps = {
  /** Switches the Settings tab, used to send billing issues to Billing. */
  goToBillingTab: () => void;
  /** Moves the MCP & API tour on to its last step on the API tab. */
  continueTourOnApiTab: () => void;
  /** A Support team member: no billing prompts or tour, only connecting. */
  supportMember?: boolean;
};

const McpManagementPanel = (props: McpManagementPanelProps) => {
  const { goToBillingTab, continueTourOnApiTab, supportMember } = props;
  const { accountData } = useSelector(getAccountState).account;
  const dispatch = useDispatch();
  const { data, loading, error, refresh } = useAiConnections();
  const [appToDisconnect, setAppToDisconnect] = useState<ConnectedAiApp>();
  const [loadingDisconnect, setLoadingDisconnect] = useState(false);
  const [alertText, setAlertText] = useState({ header: "", subHeader: "" });
  const [mcpTourStep, setMcpTourStep] = useState(1);

  const plan = useMemo(() => data?.plan, [data]);
  const apps = useMemo(() => data?.apps ?? [], [data]);

  // The tour only runs once the account has MCP access, since the setup steps
  // it walks through are hidden behind the upgrade or billing prompt until then.
  const tourReady = useMemo(
    () => !!plan?.allowed && !supportMember,
    [plan, supportMember]
  );

  const copyOnPress = useCallback(() => {
    navigator.clipboard
      .writeText(MCP_SERVER_URL)
      .then(() => {
        showNotificationBanner(
          dispatch,
          "Copied MCP URL!",
          NotificationType.AFFIRMATIVE
        );
      })
      .catch((err) => {
        alert(err);
      });
  }, [dispatch]);

  const disconnectOnPress = useCallback(async () => {
    if (!appToDisconnect) {
      return;
    }
    setLoadingDisconnect(true);
    try {
      await API.aiConnections.disconnectAiConnection({
        clientId: appToDisconnect.clientId,
      });
      showNotificationBanner(
        dispatch,
        `Disconnected ${appToDisconnect.name}`,
        NotificationType.NEGATIVE
      );
    } catch (err) {
      setAlertText({
        header: `Something went wrong disconnecting ${appToDisconnect.name}.`,
        subHeader: getApiErrorMessage(err),
      });
    }
    setLoadingDisconnect(false);
    setAppToDisconnect(undefined);
    await refresh();
  }, [appToDisconnect, dispatch, refresh]);

  const renderStep = useCallback(
    (step: number, text: string) => (
      <span className="bodyRegular">
        {step}. {text}
      </span>
    ),
    []
  );

  const renderPlanPrompt = useMemo(() => {
    if (!plan || plan.allowed || supportMember) {
      return null;
    }
    if (plan.reason === "no_plan") {
      return (
        <EmptyStateButtonSubscription
          planType={PlanTypeV4.ESSENTIAL}
          iconName="ion:sparkles"
          description={plan.message}
          btnText={
            hasSubscription(accountData)
              ? "Upgrade to Essential"
              : "Get Essential Plan"
          }
          btnIconRight={
            <OnboardingFlowDot flowTypes={[OnboardingFlowType.MCP_API]} />
          }
          containerStyles={{ paddingBlock: 30 }}
        />
      );
    }
    return (
      <div className="ColumnNormal" style={{ gap: 14, paddingInline: 14 }}>
        <span className="bodyRegular">{plan.message}</span>
        {plan.reason === "payment_failed" ? (
          <PaymentFailedButton type="Creator" onPress={goToBillingTab} />
        ) : (
          <RectangleButton
            buttonLabel="Go to Billing"
            onPress={goToBillingTab}
            altColor={Colors.BLACK}
            altTextColor={Colors.WHITE}
            containerStyles={{ padding: 14, width: "fit-content" }}
          />
        )}
      </div>
    );
  }, [accountData, goToBillingTab, plan, supportMember]);

  const renderLoadState = useMemo(() => {
    if (loading && !data) {
      return (
        <div className="Centering" style={{ paddingBlock: 30 }}>
          <CircularProgress style={{ color: Colors.GRAY1 }} size={20} />
        </div>
      );
    }
    if (error && !data) {
      return (
        <div className="ColumnNormal" style={{ gap: 10, paddingInline: 14 }}>
          <span className="smallBodySubtext" style={{ color: Colors.RED3 }}>
            {error}
          </span>
          <RectangleButton
            buttonLabel="Try Again"
            onPress={refresh}
            altColor={Colors.WHITE1}
            altTextColor={Colors.BLACK}
            containerStyles={{
              padding: 14,
              width: "fit-content",
              border: `0.5px solid ${Colors.GRAY2}`,
            }}
          />
        </div>
      );
    }
    return null;
  }, [data, error, loading, refresh]);

  return (
    <div className="ColumnNormal" style={{ margin: "auto", gap: 24 }}>
      <StandardBorderedContainer
        className="onboarding-mcp-api-server-target"
        containerStyles={{
          width: 544,
          margin: "auto",
          backgroundColor: Colors.WHITE,
          paddingBlock: 14,
        }}
      >
        <div className="ColumnNormal" style={{ gap: 7, paddingInline: 14 }}>
          <span className="sectionTitle">MCP Server</span>
          <span className="smallBodySubtext">
            Connect Markit to AI assistants like Claude so they can send texts,
            manage your audience, and more. You approve every connection with
            your Markit login.
          </span>
        </div>
        <HorizontalDivider altMargin={14} />
        {renderLoadState ?? renderPlanPrompt ?? (
          <div className="ColumnNormal" style={{ gap: 10, paddingInline: 14 }}>
            {renderStep(
              1,
              "In your AI tool, add a custom connector using this URL"
            )}
            <div className="AlignedRow" style={{ gap: 10 }}>
              <CustomTextField
                inputMode="text"
                value={MCP_SERVER_URL}
                placeholder=""
                onChange={() => {}}
                disabled
                width="100%"
                containerStyles={{
                  borderRadius: 100,
                  height: 42,
                  justifyContent: "center",
                  flex: 1,
                }}
              />
              <RectangleButton
                buttonLabel="Copy"
                onPress={copyOnPress}
                altColor={Colors.BLACK}
                altTextColor={Colors.WHITE}
                iconRight={
                  <Icon
                    icon="ion:copy-outline"
                    height={14}
                    color={Colors.WHITE}
                  />
                }
                containerStyles={{ padding: 14, width: "fit-content" }}
              />
            </div>
            {renderStep(2, "Sign in to Markit and approve the connection.")}
            {renderStep(
              3,
              supportMember
                ? "Ask your AI tool to read your conversations and reply to your contacts."
                : "Ask your AI tool to send texts, manage your audience, and create automations/products for you."
            )}
            <div
              className="AlignedRowSelect"
              style={{ gap: 2, width: "fit-content" }}
              onClick={() => window.open(MCP_DOCS_URL, "_blank")}
            >
              <span className="bodyMedium">Learn more</span>
              <Icon icon="ic:round-arrow-outward" height={16} />
            </div>
          </div>
        )}
      </StandardBorderedContainer>

      <StandardBorderedContainer
        className="onboarding-mcp-api-connected-apps-target"
        containerStyles={{
          width: 544,
          margin: "auto",
          backgroundColor: Colors.WHITE,
          paddingBlock: 14,
        }}
      >
        <div className="ColumnNormal" style={{ gap: 7, paddingInline: 14 }}>
          <span className="sectionTitle">Connected Apps</span>
          <span className="smallBodySubtext">
            AI tools you&apos;ve approved to access your Markit account.
          </span>
        </div>
        <HorizontalDivider altMargin={14} />
        {renderLoadState ??
          (apps.length === 0 ? (
            <span
              className="smallBodySubtext"
              style={{ paddingInline: 14, paddingBlock: 10 }}
            >
              No AI tools connected yet.
            </span>
          ) : (
            <div className="ColumnNormal" style={{ gap: 14 }}>
              {apps.map((app) => (
                <ConnectedAppRow
                  key={app.clientId}
                  app={app}
                  onDisconnectPress={setAppToDisconnect}
                />
              ))}
            </div>
          ))}
      </StandardBorderedContainer>

      {tourReady ? (
        mcpTourStep === 1 ? (
          <OnboardingTourStep
            key="mcp-api-tour-step-1"
            flowType={OnboardingFlowType.MCP_API}
            target=".onboarding-mcp-api-server-target"
            content="Copy this URL and add it as a custom connector in Claude, ChatGPT, or another AI tool. Then sign in to Markit and approve the connection."
            stepNumber={1}
            totalSteps={MCP_API_ONBOARDING_STEPS}
            placement="right"
            onStepComplete={() => setMcpTourStep(2)}
          />
        ) : (
          <OnboardingTourStep
            key="mcp-api-tour-step-2"
            flowType={OnboardingFlowType.MCP_API}
            target=".onboarding-mcp-api-connected-apps-target"
            content="AI tools you've connected show up here. Disconnect one anytime to cut off its access right away."
            stepNumber={2}
            totalSteps={MCP_API_ONBOARDING_STEPS}
            placement="right"
            onStepComplete={continueTourOnApiTab}
          />
        )
      ) : null}
      <ConfirmDeleteModal
        heading={`Disconnect ${appToDisconnect?.name ?? "this app"}?`}
        subtext="It will lose access to your Markit account right away. You can connect it again at any time."
        deleteButtonText="Disconnect"
        hideModal={!appToDisconnect}
        setIsVisible={(isVisible) => {
          if (!isVisible) {
            setAppToDisconnect(undefined);
          }
        }}
        deleteOnPress={disconnectOnPress}
        buttonLoading={loadingDisconnect}
      />
      <AlertContainer
        headerComp={alertText.header}
        subHeaderComp={alertText.subHeader}
        closeModal={() => setAlertText({ header: "", subHeader: "" })}
        hideModal={alertText.header === ""}
      />
    </div>
  );
};

export default McpManagementPanel;
